import React, { useState, useEffect } from "react";
import { User, Key, CreditCard, Receipt, Shield } from "lucide-react";
import AccountSettings from "./AccountSettings";
import AISettingsSection from "./AISettingsSection";
import SubscriptionSettings from "./SubscriptionSettings";
import BillingSettings from "./BillingSettings";
import SecuritySettings from "./SecuritySettings";

const Settings = ({ darkMode, user, isAuthenticated, onLogout }) => {
  const [activeSection, setActiveSection] = useState("account");
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const sections = [
    { id: "account", icon: User, label: "Account", requiresAuth: true },
    { id: "security", icon: Shield, label: "Security", requiresAuth: true },
    {
      id: "subscription",
      icon: CreditCard,
      label: "Subscription",
      requiresAuth: true,
    },
    { id: "billing", icon: Receipt, label: "Billing", requiresAuth: true },
    { id: "ai", icon: Key, label: "AI Settings", requiresAuth: false },
  ];

  // Filter sections based on auth status
  const visibleSections = sections.filter(
    (s) => !s.requiresAuth || isAuthenticated,
  );

  // Forest Twilight theme colors
  const bgColor = "#0d1a14";
  const borderColor = "#2a5038";
  const activeColor = "#d0eadb";
  const inactiveColor = "#a5d4b8";
  const activeBgColor = "#c5a34e";

  return (
    <div className="min-h-full flex flex-col bg-forest-800">
      {isMobile ? (
        <div
          style={{
            marginTop: "-1.5rem",
            marginBottom: "-1.5rem",
            width: "100vw",
            position: "relative",
            left: "50%",
            right: "50%",
            marginLeft: "-50vw",
            marginRight: "-50vw",
          }}
        >
          {/* Mobile Horizontal Nav Bar - Styled like BottomNav */}
          <nav
            className="shadow-lg"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: bgColor,
              borderBottom: "1px solid " + borderColor,
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
                alignItems: "center",
                minHeight: "64px",
                padding: "0 4px",
                width: "100%",
              }}
            >
              {visibleSections.map(({ id, icon: Icon, label }) => {
                const isActive = activeSection === id;
                return (
                  <button
                    key={id}
                    onClick={() => setActiveSection(id)}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: 1,
                      height: "64px",
                      minHeight: "64px",
                      minWidth: "48px",
                      border: "none",
                      background: isActive ? activeBgColor : "transparent",
                      borderRadius: "8px",
                      cursor: "pointer",
                      color: isActive ? activeColor : inactiveColor,
                      padding: "8px 4px",
                    }}
                    aria-label={label}
                  >
                    <Icon
                      style={{
                        width: "24px",
                        height: "24px",
                        minWidth: "24px",
                        minHeight: "24px",
                        marginBottom: "4px",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 500,
                        textAlign: "center",
                        lineHeight: "1.2",
                      }}
                    >
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Mobile Content */}
          <div className="overflow-y-auto">
            <div
              style={{
                paddingTop: "1.25rem",
                paddingBottom: "1.25rem",
                paddingLeft: "2rem",
                paddingRight: "2rem",
              }}
            >
              {activeSection === "account" && (
                <AccountSettings
                  darkMode={darkMode}
                  user={user}
                  onLogout={onLogout}
                />
              )}
              {activeSection === "security" && (
                <SecuritySettings darkMode={darkMode} user={user} />
              )}
              {activeSection === "subscription" && (
                <SubscriptionSettings darkMode={darkMode} user={user} />
              )}
              {activeSection === "billing" && (
                <BillingSettings darkMode={darkMode} user={user} />
              )}
              {activeSection === "ai" && (
                <AISettingsSection darkMode={darkMode} />
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Desktop Layout with Sidebar */}
          <div className="flex-1 flex flex-row overflow-hidden">
            {/* Sidebar */}
            <div className="w-48 bg-forest-800 border-forest-600 border-r">
              <div className="p-2 space-y-1">
                {visibleSections.map(({ id, icon: Icon, label }) => (
                  <button
                    key={id}
                    onClick={() => setActiveSection(id)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left ${
                      activeSection === id
                        ? "bg-gold-500 text-forest-100"
                        : "text-forest-200 hover:bg-forest-700 bg-forest-700"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Desktop Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-4">
                {activeSection === "account" && (
                  <AccountSettings
                    darkMode={darkMode}
                    user={user}
                    onLogout={onLogout}
                  />
                )}
                {activeSection === "security" && (
                  <SecuritySettings darkMode={darkMode} user={user} />
                )}
                {activeSection === "subscription" && (
                  <SubscriptionSettings darkMode={darkMode} user={user} />
                )}
                {activeSection === "billing" && (
                  <BillingSettings darkMode={darkMode} user={user} />
                )}
                {activeSection === "ai" && (
                  <AISettingsSection darkMode={darkMode} />
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Settings;
